/**
 * V2 admin · Owners & access.
 *
 * The single place OWNERS are managed: one row per owner, built by joining the
 * BILLING record (`lessors`) with the PORTAL ACCOUNT (`profiles` +
 * `user_roles`). These used to be two separate screens reading half the picture
 * each, so an owner could sit on one and be missing from the other. Rows
 * missing a half are shown and labelled rather than dropped.
 *
 * The page had three tabs. It now has none:
 *
 *   - Directory (everyone with a login) was never an owner screen. It is its
 *     own page at /admin/directory, under "More". `?tab=directory` redirects.
 *   - Property approvals moved off this page entirely — owner-submitted
 *     properties surface in the approvals rail on the Properties page, which
 *     is the one queue to work from instead of two overlapping views.
 *
 * Nothing was migrated to make this work: both halves are read where they
 * already live, and every edit writes back to the record it came from.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Navigate, useSearchParams } from "react-router-dom";
import {
  Building2,
  Plus,
  Users,
  Home,
  BadgeCheck,
  Banknote,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { AdminV2Layout } from "@/components/admin/v2/AdminV2Layout";
import { usePaywall } from "@/components/paywall/PaywallProvider";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import {
  AdminCreateOwnerAccountCard,
  EditUserDialog,
} from "@/components/admin/AdminRolePanels";
import type { AdminProfileDirectoryRow } from "@/lib/fetch-admin-profiles-directory";
import {
  countOwnerStatusFilters,
  findCurrentOwnerRow,
  isTestOwner,
  matchesOwnerStatusFilter,
  ownerStatusFilterHint,
  ownerStatusFilterLabel,
  OWNER_STATUS_FILTERS,
  type OwnerStatusFilter,
  sortOwnerDirectory,
} from "@/lib/owner-directory";
import {
  useOwnersDirectory,
  type OwnerDirectoryListRow,
} from "@/hooks/v2/useOwnersDirectory";
import { LessorFormDialog } from "@/components/admin/v2/LessorFormDialog";
import { OwnersTable } from "@/components/admin/v2/OwnersTable";
import { LessorDeleteDialog } from "@/components/admin/v2/LessorDeleteDialog";
import { OwnerDetailModal } from "@/components/admin/v2/OwnerDetailModal";
import { OwnerActionsMenu } from "@/components/admin/v2/OwnerActionsMenu";
import { DebouncedSearchInput } from "@/components/admin/v2/primitives/DebouncedSearchInput";
import { OwnerPayoutsView } from "@/components/admin/v2/OwnerPayoutsView";
import { countReadyPayoutGroups } from "@/lib/owner-payouts";
import { EmptyState } from "@/components/admin/v2/primitives/EmptyState";
import { TableSkeleton } from "@/components/admin/v2/primitives/TableSkeleton";
import {
  useCreateLessor,
  useDeleteLessor,
  useLessorOpenInvoiceCounts,
  useUpdateLessor,
} from "@/hooks/v2/useLessors";
import { type OwnerListRow } from "@/hooks/v2/useOwnerDetail";
import { invalidateOwnerCaches } from "@/hooks/v2/owner-detail-keys";
import { usePaged } from "@/hooks/usePaged";
import { sendUserLoginLink } from "@/lib/admin-account-actions";
import { cn } from "@/lib/utils";
import type { Lessor, LessorInput } from "@/types/lessor";
import { useLanguage } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import { useOperator } from "@/hooks/useOperator";
import { useOperatorBranding } from "@/hooks/useOperatorBranding";
import { useTestAccountFilters } from "@/hooks/v2/useTestAccountFilters";

/** Owners shown per page. */
const PAGE_SIZE = 15;

const TABLE_COLS = [
  { label: "Owner", bar: "w-40" },
  { label: "Type", className: "hidden sm:table-cell", bar: "w-16" },
  { label: "Apartments", className: "text-center", bar: "w-8 mx-auto" },
  { label: "Property", bar: "w-16" },
  { label: "Status", bar: "w-20" },
  { label: "Actions", className: "w-[90px] text-right", bar: "w-12 ml-auto" },
];

export default function V2LessorsListPage() {
  const { isOwnerPortalOnly, isSuperAdmin, isOperatorPortal } = useAuth();
  // properties are public-read; scope the picker lookup to an operator's own org.
  const { operator } = useOperator();
  const operatorId = isOperatorPortal ? (operator?.id ?? null) : null;
  const { guardCreate } = usePaywall();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  const [formMode, setFormMode] = useState<"create" | "edit">("create");
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<Lessor | null>(null);
  const [deleting, setDeleting] = useState<OwnerListRow | null>(null);
  // The whole merged row, not just its billing half: an owner who only has a
  // portal account has no lessor to hold here, and they open a profile too.
  const [viewing, setViewing] = useState<OwnerDirectoryListRow | null>(null);
  // Account-based Add/Edit (merged with the lessor entity): the Add form and
  // Edit dialog are the same ones used in Settings, keyed off portal accounts.
  const [createOpen, setCreateOpen] = useState(false);
  // "Create login" on a billing-only row: that row's lessor, preselected in
  // the Add owner form. Null for the plain Add owner button.
  const [createForLessorId, setCreateForLessorId] = useState<string | null>(
    null
  );
  const [editAccountRow, setEditAccountRow] =
    useState<AdminProfileDirectoryRow | null>(null);
  // KPI-tile driven filter (replaces the old status tabs).
  const [activeFilter, setActiveFilter] = useState<
    "apartments" | "onboarded" | "payouts" | null
  >(null);
  // Where the owner actually stands — no login yet, onboarding not signed yet
  // (COL-486). Independent flags, so one owner can sit under both chips.
  const [statusFilter, setStatusFilter] = useState<OwnerStatusFilter | null>(
    null
  );
  // Test/CoLiviq owners hidden unless this is on — same toggle and default as
  // Bookings & Tenants and Analytics (COL-485). The predicate is isTestOwner:
  // their email rule on BOTH halves plus the demo flag, deliberately.
  const [includeTestAccounts, setIncludeTestAccounts] = useState(false);
  const { toast } = useToast();
  const { t } = useLanguage();
  const { brandText } = useOperatorBranding();

  const {
    rows: searchedRows,
    allRows: everyOwnerRow,
    lessors: owners,
    accounts,
    isLoading: ownersLoading,
    accountsLoading,
    error,
  } = useOwnersDirectory(search, { enabled: !isOwnerPortalOnly });

  // Dynamically-added test emails (Directory-managed), combined with the
  // static domain/seed rules in isTestAccount() and the demo flag.
  // Gated like the owners query above: owner-portal users are redirected off
  // this page, and the table behind this is admin-only.
  const { data: testAccountFilters, isLoading: testFiltersLoading } =
    useTestAccountFilters({ enabled: !isOwnerPortalOnly });
  const dynamicTestEmails = useMemo(
    () => (testAccountFilters ?? []).map((f) => f.email),
    [testAccountFilters]
  );

  // Both row sets below lose their test owners here, once, so the KPI tiles,
  // status chips, table, pagination and payouts view all agree on what's
  // excluded. `everyOwnerRow` stays unfiltered for the deep-link and profile
  // re-sync effects: an owner opened by id should open whatever the toggle.
  //
  // While the toggle is off, nothing is shown until everything that decides
  // "test owner" has arrived: the Directory list and the account half (its
  // emails and demo flag). The billing half alone would briefly list — and
  // count — test owners the default says to hide. A failed Directory read
  // doesn't hold the page; the static domain/seed rules still apply, as on
  // Bookings & Tenants.
  const testRulesPending =
    !includeTestAccounts && (testFiltersLoading || accountsLoading);
  const isLoading = ownersLoading || testRulesPending;
  const withoutTestOwners = useCallback(
    (rows: OwnerDirectoryListRow[]) => {
      if (includeTestAccounts) return rows;
      if (testRulesPending) return [];
      return rows.filter((r) => !isTestOwner(r, dynamicTestEmails));
    },
    [includeTestAccounts, testRulesPending, dynamicTestEmails]
  );
  const allRows = useMemo(
    () => withoutTestOwners(everyOwnerRow),
    [withoutTestOwners, everyOwnerRow]
  );
  // Newest signups first (Neel, 2026-10-07). Sorted AFTER the test-owner
  // filter so the demo pin can only apply to a row that survived it: with the
  // toggle off the demo owner is already gone, and `pinDemoFirst` is moot.
  const directoryRows = useMemo(
    () =>
      sortOwnerDirectory(withoutTestOwners(searchedRows), {
        pinDemoFirst: includeTestAccounts,
      }),
    [withoutTestOwners, searchedRows, includeTestAccounts]
  );
  const testOwnerCount = testRulesPending
    ? 0
    : everyOwnerRow.length - allRows.length;

  // Property list for the Add / Edit "Assign to property" pickers.
  const { data: propertiesList } = useQuery({
    queryKey: ["admin-role-mgmt-properties", operatorId],
    enabled: !isOwnerPortalOnly && (!isOperatorPortal || operatorId != null),
    queryFn: async () => {
      let q = supabase
        .from("properties")
        .select("id, title, city")
        .order("title");
      if (operatorId) q = q.eq("operator_id", operatorId);
      const { data, error: e } = await q;
      if (e) throw e;
      return (data ?? []) as { id: number; title: string; city: string }[];
    },
  });

  /**
   * Property titles by id — the account half stores property IDS only, so this
   * is what lets an owner who has a login but no billing entity still show
   * which buildings they are attached to.
   */
  const propertiesById = useMemo(() => {
    const map = new Map<
      number,
      { title: string | null; city: string | null }
    >();
    for (const p of propertiesList ?? [])
      map.set(Number(p.id), { title: p.title, city: p.city });
    return map;
  }, [propertiesList]);

  // Deep-link support: `?edit=<id>` (e.g. from the property global search's
  // owner results) opens that owner's edit sheet. The param is consumed
  // once and cleared so a reload doesn't reopen it.
  const [searchParams, setSearchParams] = useSearchParams();
  const editId = searchParams.get("edit");
  useEffect(() => {
    if (!editId || !owners) return;
    const target = owners.find((o) => o.id === editId);
    if (target) {
      setFormMode("edit");
      setEditing(target);
      setFormOpen(true);
    }
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete("edit");
        return next;
      },
      { replace: true }
    );
  }, [editId, owners, setSearchParams]);

  // `?owner=<id>` (e.g. from the Payments hub owner links) opens that owner's
  // detail modal. Consumed once and cleared so a reload doesn't reopen it.
  const ownerId = searchParams.get("owner");
  useEffect(() => {
    // Guarded on the BILLING half having loaded (not on everyOwnerRow being
    // non-empty) so a genuinely empty list still consumes the param instead of
    // leaving it in the URL to fire again on the next render.
    if (!ownerId || !owners) return;
    const target = everyOwnerRow.find((r) => r.lessor?.id === ownerId);
    if (target) setViewing(target);
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete("owner");
        return next;
      },
      { replace: true }
    );
  }, [ownerId, owners, everyOwnerRow, setSearchParams]);

  /**
   * Re-point the open owner profile at the freshly fetched row.
   *
   * `viewing` is the row object captured at click time, and OwnerDetailModal
   * seeds its editable copy from it — so a save (here, from the Payments hub,
   * or by the owner themselves in Settings) refreshed the list underneath while
   * the modal kept rendering the pre-save owner. Same snapshot problem the
   * tenant profile has, and the same fix: when the list refetches, hand the
   * modal the new row. An owner that has disappeared (deleted, or filtered out
   * by the current search) keeps the snapshot rather than blanking the modal.
   *
   * Looked up by key, then by account (findCurrentOwnerRow): linking an
   * account-only owner to a billing record — which the profile's own header
   * menu can do, via "Edit login & access" — moves them to a new key, and a
   * key-only match left the profile saying there was no billing record.
   */
  useEffect(() => {
    if (!owners) return;
    setViewing((prev) =>
      prev ? (findCurrentOwnerRow(everyOwnerRow, prev) ?? prev) : prev
    );
  }, [owners, everyOwnerRow]);

  const createMut = useCreateLessor();
  const updateMut = useUpdateLessor();
  const deleteMut = useDeleteLessor();
  const { data: openInvoiceCounts } = useLessorOpenInvoiceCounts();

  const ownerList = directoryRows;

  /** Chip counts — over every owner, so a chip never reads 0 because of a tile.
   *  Empty in the payouts view, which hides the status chips: they describe an
   *  owner's record, not a payout (COL-474). */
  const statusCounts = useMemo(
    () => countOwnerStatusFilters(activeFilter === "payouts" ? [] : allRows),
    [allRows, activeFilter]
  );
  /** A chip nobody is in any more is hidden (e.g. after "Create login" on the
   *  last login-pending owner) — so it must stop filtering too, or the list
   *  stays narrowed with no pressed chip to explain or undo it. */
  const activeStatusFilter =
    statusFilter && statusCounts[statusFilter] > 0 ? statusFilter : null;

  const filteredOwners = useMemo(() => {
    // KPI tiles read the BILLING half — apartments, invoices and property
    // onboarding only exist against a lessor — so an owner without one falls
    // outside every tile except "All owners".
    const byTile = (() => {
      switch (activeFilter) {
        case "apartments":
          return ownerList.filter((r) => (r.lessor?.apartmentsCount ?? 0) > 0);
        case "onboarded":
          return ownerList.filter(
            (r) => r.lessor?.onboardingStage === "onboarded"
          );
        // Every owner with a payout to list (COL-474), this month's or carried
        // over from an earlier one (COL-515) — whatever its status, which the
        // payouts table shows per row.
        case "payouts":
          return ownerList.filter((r) =>
            [
              ...(r.lessor?.payoutGroups ?? []),
              ...(r.lessor?.carriedOverPayoutGroups ?? []),
            ].some((g) => g.status !== null)
          );
        default:
          return ownerList;
      }
    })();
    // Status chips describe an owner, not a payout, so they do not apply to
    // the payouts view (see statusCounts below).
    return activeStatusFilter && activeFilter !== "payouts"
      ? byTile.filter((r) => matchesOwnerStatusFilter(r, activeStatusFilter))
      : byTile;
  }, [ownerList, activeFilter, activeStatusFilter]);
  /** The owner rows the owners table shows — none in the payouts view, which
   *  lists payouts (OwnerPayoutsView) instead of owners. */
  const tableOwners = activeFilter === "payouts" ? [] : filteredOwners;
  /** A failed payout read leaves every owner with no payout groups — unknown,
   *  not "nothing owed" — so the tile shows "—" and the payouts view an error,
   *  instead of a 0 or an empty list an admin could act on (COL-474). Read off
   *  every owner, not the searched or test-filtered ones: the flag is per fetch. */
  const payoutDataDegraded = everyOwnerRow.some(
    (r) => r.lessor?.payoutDataDegraded
  );
  const payoutsDegraded = activeFilter === "payouts" && payoutDataDegraded;

  // Pagination — shared usePaged hook (same as PaymentsHub): it clamps to the
  // LAST valid page when the filtered set shrinks, so a misfiltered view never
  // lands on a blank page or jumps back to page 1.
  const {
    page,
    setPage,
    totalPages,
    pageRows: pagedOwners,
    total: pagedTotal,
  } = usePaged(filteredOwners, PAGE_SIZE);
  // Reset to page 1 when the filter or search *changes* meaning (distinct from
  // the set merely shrinking, which the hook's clamp already handles).
  useEffect(() => {
    setPage(1);
  }, [search, activeFilter, activeStatusFilter, includeTestAccounts, setPage]);

  /**
   * The page has no tabs left. `?tab=` survives only as an address other places
   * still link to: `directory` is redirected below.
   */
  const requestedTab = searchParams.get("tab");

  // KPI counts for the summary strip. Each value matches the row set its tile
  // filters to (so the number always equals what a click reveals) — EXCEPT
  // "Apartments" (a portfolio total, not a row count) and "payouts ready",
  // whose count is property-level payout GROUPS (COL-461), which can outnumber
  // the owners the click reveals when an owner has more than one ready group —
  // and the click lists every payout, whatever its status (COL-520).
  const summary = useMemo(() => {
    const apartments = allRows.reduce(
      (n, r) => n + (r.lessor?.apartmentsCount ?? 0),
      0
    );
    const onboarded = allRows.filter(
      (r) => r.lessor?.onboardingStage === "onboarded"
    ).length;
    // Ready payouts of every month: this month's plus the ones carried over
    // from earlier months (COL-515), so the number counts every Ready payout
    // the payouts table lists.
    const readyPayouts = allRows.reduce(
      (n, r) =>
        n +
        (r.lessor?.readyPayoutGroups ?? 0) +
        countReadyPayoutGroups(r.lessor?.carriedOverPayoutGroups ?? []),
      0
    );
    return {
      // Every owner, including the ones that only have a portal account — the
      // whole point of the merge is that this number stops under-reporting.
      owners: allRows.length,
      apartments,
      onboarded,
      readyPayouts,
    };
  }, [allRows]);

  if (isOwnerPortalOnly) {
    return <Navigate to="/admin/properties" replace />;
  }

  // Directory left this page entirely, and its old address still arrives here.
  // Send those on rather than dropping people on the owners list, which is not
  // what they asked for.
  if (requestedTab === "directory") {
    return <Navigate to="/admin/directory" replace />;
  }

  const hasSearch = search.trim().length > 0;
  // Read before the test-owner filter: a list that is ALL test owners is not
  // "no owners yet", and treating it as one would hide the toggle that shows them.
  const isInitialEmpty = !isLoading && !hasSearch && everyOwnerRow.length === 0;
  const isSearchEmpty = !isLoading && hasSearch && ownerList.length === 0;
  // Owners exist, but the toggle hides every one of them — a state the page
  // could not reach before it, so nothing else here says why it is blank.
  const isAllTestOwners =
    !isLoading && !hasSearch && !isInitialEmpty && ownerList.length === 0;

  function handleCreateClick() {
    // Add owner = the account-creation flow (creates a portal account + links
    // it to a property). Also offers linking to / defaults from a lessor entity.
    // Unpaid operators get the paywall instead (COL-352); RLS is the backstop.
    guardCreate("create_owner", () => {
      setCreateForLessorId(null);
      setCreateOpen(true);
    });
  }

  /**
   * Edit the PORTAL ACCOUNT (role, property assignments, contracts).
   *
   * Its own menu item, separate from "Edit owner": the two edit different
   * records, and having one item open whichever half happened to exist meant
   * "Edit owner" could open a dialog headed "Edit user".
   */
  function handleEditAccount(row: OwnerDirectoryListRow) {
    if (row.account) setEditAccountRow(row.account);
  }

  /** Edit the owner RECORD (lessor: name, type, representor, bank). */
  function handleEditEntity(row: OwnerDirectoryListRow) {
    if (!row.lessor) return;
    setFormMode("edit");
    setEditing(row.lessor);
    setFormOpen(true);
  }

  /**
   * Open the owner's profile — EVERY row, whichever halves it has.
   *
   * This used to branch: a row with no billing record opened the account EDIT
   * form instead, so clicking a row gave you a profile or a form depending on
   * something the admin can't see from the outside. The modal now renders the
   * account-only shape itself (see OwnerAccountOnlyProfile), so a click always
   * means "show me this owner". Editing lives on the ⋯ menu.
   */
  function handleRowClick(row: OwnerDirectoryListRow) {
    setViewing(row);
  }

  /**
   * Fill in the missing half of an owner's record.
   *
   * Neither branch invents data: "Create login" opens the same account-creation
   * form as Add owner, and "Add billing entity" opens the same owner form, both
   * prefilled with what we already know.
   */
  function handleFixLink(row: OwnerDirectoryListRow) {
    if (row.linkState === "billing_only") {
      setCreateForLessorId(row.lessor?.id ?? null);
      setCreateOpen(true);
      return;
    }
    setFormMode("create");
    setEditing(null);
    setFormOpen(true);
  }

  /**
   * "Add billing entity" from inside the profile — its no-billing-record note
   * and its header ⋯ menu. Same flow as the row's link, but close the profile
   * first: the form replaces it, rather than stacking a dialog over a dialog
   * for the record the profile is about to be about.
   */
  function addBillingEntityFromProfile() {
    const row = viewing;
    setViewing(null);
    if (row) handleFixLink(row);
  }

  function handleSubmit(input: LessorInput, reason?: string) {
    if (formMode === "create") {
      createMut.mutate(input, {
        onSuccess: (lessor) => {
          toast({
            title: t("admin.owners.list.ownerAdded", "Owner added"),
            description: lessor.name,
          });
          setFormOpen(false);
        },
        onError: (err) => {
          toast({
            title: t("admin.owners.list.addFailed", "Could not add owner"),
            description:
              err instanceof Error
                ? err.message
                : t("admin.owners.list.unknownError", "Unknown error"),
            variant: "destructive",
          });
        },
      });
    } else if (editing) {
      updateMut.mutate(
        { id: editing.id, input, reason },
        {
          onSuccess: (lessor) => {
            toast({
              title: t("admin.owners.list.changesSaved", "Changes saved"),
              description: lessor.name,
            });
            setFormOpen(false);
          },
          onError: (err) => {
            toast({
              title: t(
                "admin.owners.list.saveFailed",
                "Could not save changes"
              ),
              description:
                err instanceof Error
                  ? err.message
                  : t("admin.owners.list.unknownError", "Unknown error"),
              variant: "destructive",
            });
          },
        }
      );
    }
  }

  async function handleSendLoginLink(row: OwnerDirectoryListRow) {
    const owner = { email: row.email, name: row.displayName };
    if (!owner.email) return;
    // Use the shared helper so this matches the Settings panel exactly — it
    // sends app_url (needed to build a working link) and unwraps edge-function
    // error bodies. The old inline invoke omitted app_url.
    const res = await sendUserLoginLink({
      email: owner.email,
      name: owner.name ?? undefined,
    });
    if (res.ok) {
      toast({
        title: t("admin.owners.list.loginLinkSent", "Login link sent"),
        description: owner.email,
      });
    } else {
      toast({
        title: t(
          "admin.owners.list.loginLinkFailed",
          "Could not send login link"
        ),
        description:
          res.error ?? t("admin.owners.list.unknownError", "Unknown error"),
        variant: "destructive",
      });
    }
  }

  function handleConfirmDelete(reason: string) {
    if (!deleting) return;
    // Reason is persisted to audit_logs inside the mutation — the owner record
    // is gone, so this is the only trail of who deleted it and why.
    deleteMut.mutate(
      { id: deleting.id, reason },
      {
        onSuccess: () => {
          toast({
            title: `${deleting.name} ${t("admin.owners.list.deletedSuffix", "deleted")}`,
            description: t(
              "admin.owners.list.deletedDescription",
              "Linked apartments and properties keep existing with no owner assigned."
            ),
          });
          setDeleting(null);
          // Deleted from the open profile's ⋯ menu (COL-475): close it. The
          // re-sync above can't — the row is gone, or is account-only now under
          // a different key — so it would keep showing the deleted record.
          setViewing((v) => (v?.lessor?.id === deleting.id ? null : v));
        },
        onError: (err) => {
          toast({
            title: t(
              "admin.owners.list.deleteFailed",
              "Could not delete owner"
            ),
            description:
              err instanceof Error
                ? err.message
                : t("admin.owners.list.unknownError", "Unknown error"),
            variant: "destructive",
          });
        },
      }
    );
  }

  return (
    <AdminV2Layout
      title={t("admin.nav.owners", "Owners")}
      surface
      description={t(
        "admin.owners.list.pageDescription",
        "Manage owner accounts and billing access."
      )}
      actions={
        // Same button spec as "+ Create Tenant" on Bookings & Tenants
        // (AdminTenants.tsx), so the two list pages' primary action reads as
        // one control rather than two sizes of the same idea.
        <Button
          size="sm"
          className="gap-1.5 h-9 rounded-xl shrink-0"
          onClick={handleCreateClick}
        >
          <Plus className="w-4 h-4" />
          {t("admin.owner.addAction", "Add owner")}
        </Button>
      }
    >
      <div className="space-y-5">
        {/* Portfolio KPIs */}
        {!isInitialEmpty && (
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            <StatTile
              icon={Users}
              label={t("admin.owners.list.allOwners", "All owners")}
              value={summary.owners}
              tint="text-teal-600 bg-teal-50"
              active={activeFilter === null}
              onClick={() => setActiveFilter(null)}
            />
            <StatTile
              icon={Home}
              label={t("admin.owners.col.apartments", "Apartments")}
              value={summary.apartments}
              tint="text-indigo-600 bg-indigo-50"
              active={activeFilter === "apartments"}
              onClick={() =>
                setActiveFilter((f) =>
                  f === "apartments" ? null : "apartments"
                )
              }
            />
            <StatTile
              icon={BadgeCheck}
              label={t("admin.owners.stage.onboarded", "Onboarded")}
              value={summary.onboarded}
              tint="text-emerald-600 bg-emerald-50"
              active={activeFilter === "onboarded"}
              onClick={() =>
                setActiveFilter((f) => (f === "onboarded" ? null : "onboarded"))
              }
            />
            <StatTile
              icon={Banknote}
              label={t("admin.owners.list.payoutsReadyAll", "Payouts ready")}
              value={payoutDataDegraded ? "—" : summary.readyPayouts}
              tint="text-amber-600 bg-amber-50"
              alert={!payoutDataDegraded && summary.readyPayouts > 0}
              active={activeFilter === "payouts"}
              onClick={() =>
                setActiveFilter((f) => (f === "payouts" ? null : "payouts"))
              }
            />
          </div>
        )}

        <div className="space-y-5">
          {/* Filters and search share one row from lg up — stacked they cost a
              whole row of vertical space for two controls that fit side by
              side on any admin-sized screen. */}
          {!isInitialEmpty && (
            <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
              {/* Status chips — where each owner actually stands (COL-486).
                  A chip with nobody in it is hidden, so a healthy list stays
                  quiet. The two overlap: an owner who has neither a login nor
                  a signed onboarding is under both. */}
              {statusCounts.all > 0 && (
                <div className="flex flex-wrap items-center gap-2 lg:shrink-0">
                  <button
                    type="button"
                    onClick={() => setStatusFilter(null)}
                    aria-pressed={activeStatusFilter === null}
                    className={cn(
                      "rounded-full border px-3 py-1 text-xs font-medium transition-colors",
                      activeStatusFilter === null
                        ? "border-primary bg-primary text-primary-foreground"
                        : "bg-card hover:bg-muted"
                    )}
                  >
                    {t("common.all", "All")}{" "}
                    <span className="tabular-nums opacity-70">
                      {statusCounts.all}
                    </span>
                  </button>
                  {OWNER_STATUS_FILTERS.map((filter) =>
                    statusCounts[filter] > 0 ? (
                      <button
                        key={filter}
                        type="button"
                        onClick={() =>
                          setStatusFilter((f) => (f === filter ? null : filter))
                        }
                        aria-pressed={activeStatusFilter === filter}
                        title={ownerStatusFilterHint(t, filter)}
                        className={cn(
                          "rounded-full border px-3 py-1 text-xs font-medium transition-colors",
                          activeStatusFilter === filter
                            ? "border-primary bg-primary text-primary-foreground"
                            : "bg-card hover:bg-muted"
                        )}
                      >
                        {ownerStatusFilterLabel(t, filter)}{" "}
                        <span className="tabular-nums opacity-70">
                          {statusCounts[filter]}
                        </span>
                      </button>
                    ) : null
                  )}
                </div>
              )}

              {/* Search — takes whatever width the chips leave. */}
              <div className="min-w-0 lg:flex-1">
                <DebouncedSearchInput
                  value={search}
                  onDebouncedChange={setSearch}
                  placeholder={t(
                    "admin.field.searchOwners",
                    "Search owners by name or email…"
                  )}
                  inputId="owners-search"
                  maxWidthClass="max-w-none"
                />
              </div>

              {/* Test/CoLiviq accounts toggle — same control as Analytics',
                  inline in the filter row. The count is how many it hides. */}
              {/* Wraps below lg: the German label is wider than a phone row. */}
              <div className="flex min-h-10 items-center gap-2.5 rounded-xl border bg-background px-3 py-2 lg:h-10 lg:shrink-0 lg:py-0 lg:whitespace-nowrap">
                <Switch
                  className="shrink-0"
                  id="owners-include-test-accounts"
                  checked={includeTestAccounts}
                  onCheckedChange={setIncludeTestAccounts}
                />
                <Label
                  htmlFor="owners-include-test-accounts"
                  className="text-sm text-muted-foreground cursor-pointer"
                >
                  {brandText(
                    t(
                      "admin.tenants.includeTestAccounts",
                      "Include test/coliviq accounts"
                    )
                  )}
                  {testOwnerCount > 0 ? ` (${testOwnerCount})` : ""}
                </Label>
              </div>
            </div>
          )}

          {error && (
            <Card className="p-4 border-destructive bg-destructive/5">
              <p className="text-sm font-medium">
                {t("admin.error.couldNotLoadOwners", "Could not load owners")}
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                {error instanceof Error
                  ? error.message
                  : t("admin.owners.list.unknownError", "Unknown error")}
              </p>
            </Card>
          )}

          {isLoading && <TableSkeleton columns={TABLE_COLS} rows={5} />}

          {isInitialEmpty && (
            <Card className="border-dashed">
              <EmptyState
                icon={Building2}
                title={t("admin.empty.noOwnersTitle", "No owners yet")}
                description={t(
                  "admin.empty.noOwnersDesc",
                  "Create your first owner — a property owner record with default representor and bank details. Owners get linked to properties through the wizard, and their defaults flow into contracts and payouts automatically."
                )}
                actions={
                  <Button onClick={handleCreateClick}>
                    <Plus className="mr-2 h-4 w-4" />
                    {t("admin.owner.addAction", "Add owner")}
                  </Button>
                }
              />
            </Card>
          )}

          {isSearchEmpty && (
            <Card className="border-dashed">
              <EmptyState
                title={`${t("admin.owners.list.noMatches", "No matches")} "${search}"`}
                description={t(
                  "admin.owners.list.noMatchesHint",
                  "Try a different name or email, or clear the search to see all results."
                )}
                actions={
                  // Wraps: two German button labels overflow a phone row.
                  <div className="flex flex-wrap justify-center gap-2">
                    <Button variant="ghost" onClick={() => setSearch("")}>
                      {t("admin.owners.list.clearSearch", "Clear search")}
                    </Button>
                    {/* The search did match — only test owners, which the toggle
                        hides. Clearing the search would not surface them. */}
                    {searchedRows.length > 0 && (
                      <Button
                        variant="ghost"
                        onClick={() => setIncludeTestAccounts(true)}
                      >
                        {brandText(
                          t(
                            "admin.tenants.includeTestAccounts",
                            "Include test/coliviq accounts"
                          )
                        )}
                      </Button>
                    )}
                  </div>
                }
                compact
              />
            </Card>
          )}

          {isAllTestOwners && !payoutsDegraded && (
            <Card className="border-dashed">
              <EmptyState
                title={t(
                  "admin.owners.list.noneMatchView",
                  "No owners match this view"
                )}
                actions={
                  <Button
                    variant="ghost"
                    onClick={() => setIncludeTestAccounts(true)}
                  >
                    {brandText(
                      t(
                        "admin.tenants.includeTestAccounts",
                        "Include test/coliviq accounts"
                      )
                    )}
                  </Button>
                }
                compact
              />
            </Card>
          )}

          {!isLoading && payoutsDegraded && (
            <Card className="p-4 border-destructive bg-destructive/5">
              <p className="text-sm font-medium">
                {t(
                  "admin.owners.payouts.degradedTitle",
                  "Payouts could not be loaded"
                )}
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                {t(
                  "admin.owners.payouts.degradedDescription",
                  "Some rent or payment data did not load, so this view could be missing payouts. Try again before acting on it."
                )}
              </p>
              <Button
                variant="outline"
                size="sm"
                className="mt-3 h-8"
                onClick={() =>
                  queryClient.invalidateQueries({
                    queryKey: ["v2", "owners-list"],
                  })
                }
              >
                {t("admin.owners.payouts.retry", "Try again")}
              </Button>
            </Card>
          )}

          {!isLoading &&
            !isSearchEmpty &&
            activeFilter &&
            !payoutsDegraded &&
            ownerList.length > 0 &&
            filteredOwners.length === 0 && (
              <Card className="border-dashed">
                <EmptyState
                  title={t(
                    "admin.owners.list.noneMatchView",
                    "No owners match this view"
                  )}
                  description={t(
                    "admin.owners.list.noneUnderCard",
                    "No owners fall under the selected summary card."
                  )}
                  actions={
                    <Button
                      variant="ghost"
                      onClick={() => setActiveFilter(null)}
                    >
                      {t("admin.owners.list.showAllOwners", "Show all owners")}
                    </Button>
                  }
                  compact
                />
              </Card>
            )}

          {!isLoading &&
            activeFilter === "payouts" &&
            !payoutsDegraded &&
            filteredOwners.length > 0 && (
              <OwnerPayoutsView
                rows={filteredOwners}
                onOpenOwner={handleRowClick}
              />
            )}

          {!isLoading && tableOwners.length > 0 && (
            <>
              <OwnersTable
                rows={pagedOwners}
                onRowClick={handleRowClick}
                onEditOwner={handleEditEntity}
                onEditAccount={handleEditAccount}
                onDelete={(row) => row.lessor && setDeleting(row.lessor)}
                onSendLoginLink={handleSendLoginLink}
                onFixLink={handleFixLink}
                propertiesById={propertiesById}
                isSuperAdmin={isSuperAdmin}
              />
              {pagedTotal > PAGE_SIZE && (
                <div className="flex items-center justify-between px-1 text-sm">
                  <span className="text-muted-foreground">
                    {(page - 1) * PAGE_SIZE + 1}–
                    {Math.min(page * PAGE_SIZE, pagedTotal)}{" "}
                    {t("admin.roles.mgmt.of", "of")} {pagedTotal}
                  </span>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-8 gap-1"
                      disabled={page <= 1}
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                    >
                      <ChevronLeft className="h-4 w-4" />{" "}
                      {t("admin.owners.list.prev", "Prev")}
                    </Button>
                    <span className="tabular-nums text-muted-foreground">
                      {page} / {totalPages}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-8 gap-1"
                      disabled={page >= totalPages}
                      onClick={() =>
                        setPage((p) => Math.min(totalPages, p + 1))
                      }
                    >
                      {t("admin.owners.list.next", "Next")}{" "}
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <OwnerDetailModal
        lessor={viewing?.lessor ?? null}
        account={viewing?.account ?? null}
        propertiesById={propertiesById}
        onAddBillingEntity={addBillingEntityFromProfile}
        onClose={() => setViewing(null)}
        headerActions={
          viewing ? (
            // Same menu as the table row; `viewing` is re-synced from the live
            // list above, so its items track deactivate / demo / login edits.
            // No "Open profile" or "Edit owner": one is this page, the other
            // the inline Edit button beside the menu.
            <OwnerActionsMenu
              owner={{
                name: viewing.displayName,
                type: viewing.lessor?.type ?? null,
                email: viewing.email,
                id: viewing.lessor?.id ?? null,
              }}
              account={viewing.account}
              hasEntity={!!viewing.lessor}
              isSuperAdmin={isSuperAdmin}
              onEditAccount={
                viewing.account ? () => handleEditAccount(viewing) : undefined
              }
              onAddBillingEntity={addBillingEntityFromProfile}
              onSendLoginLink={() => handleSendLoginLink(viewing)}
              onDelete={() => viewing.lessor && setDeleting(viewing.lessor)}
            />
          ) : null
        }
      />

      {/* Add owner — reuses the Settings "Create owner account" flow. */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent
          className="max-w-3xl max-h-[92vh] overflow-y-auto p-0 gap-0"
          aria-describedby={undefined}
        >
          <DialogTitle className="sr-only">
            {t("admin.owner.addAction", "Add owner")}
          </DialogTitle>
          <AdminCreateOwnerAccountCard
            // Remount per opening, so the preselected lessor is always the
            // row's that opened it.
            key={createForLessorId ?? "new"}
            initialLessorId={createForLessorId}
            onSuccess={() => setCreateOpen(false)}
          />
        </DialogContent>
      </Dialog>

      {/* Edit owner (account) — reuses the Settings "Edit user" dialog. */}
      {editAccountRow && (
        <EditUserDialog
          row={editAccountRow}
          properties={propertiesList}
          isSuperAdmin={isSuperAdmin}
          title={t(
            "admin.owners.actions.editLoginAccess",
            "Edit login & access"
          )}
          onClose={() => setEditAccountRow(null)}
          onSaved={() => {
            invalidateOwnerCaches(queryClient);
            queryClient.invalidateQueries({
              queryKey: ["admin-role-mgmt-users"],
            });
          }}
        />
      )}

      <LessorFormDialog
        open={formOpen}
        onOpenChange={(open) => {
          setFormOpen(open);
          if (!open) setEditing(null);
        }}
        mode={formMode}
        lessor={editing}
        isSubmitting={createMut.isPending || updateMut.isPending}
        onSubmit={handleSubmit}
      />

      <LessorDeleteDialog
        lessor={deleting}
        linkedPropertyCount={deleting ? deleting.propertiesCount : 0}
        linkedApartmentCount={deleting ? deleting.apartmentsCount : 0}
        openInvoiceCount={
          deleting ? (openInvoiceCounts?.[deleting.id] ?? 0) : 0
        }
        isDeleting={deleteMut.isPending}
        onConfirm={handleConfirmDelete}
        onCancel={() => setDeleting(null)}
      />
    </AdminV2Layout>
  );
}

/** Compact KPI tile for the Owners summary strip — clickable to filter the list. */
function StatTile({
  icon: Icon,
  label,
  value,
  tint,
  alert,
  active,
  onClick,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  /** "—" when the figure is unknown (a degraded read), never a stand-in 0. */
  value: number | string;
  tint: string;
  alert?: boolean;
  active?: boolean;
  onClick?: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        "group flex items-center gap-3 rounded-2xl border bg-card p-4 text-left shadow-sm transition-all",
        "hover:-translate-y-0.5 hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1",
        active
          ? "border-primary/60 ring-1 ring-primary/40"
          : "hover:border-border"
      )}
    >
      <span
        className={cn(
          "flex h-10 w-10 shrink-0 items-center justify-center rounded-xl transition-transform duration-200 group-hover:scale-110 group-hover:-rotate-3",
          tint
        )}
      >
        <Icon className="h-5 w-5" />
      </span>
      <div className="min-w-0">
        <div className="flex items-center gap-1.5">
          <span className="text-2xl font-semibold tabular-nums leading-none">
            {value}
          </span>
          {alert && (
            <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-amber-500" />
          )}
        </div>
        <p className="mt-1 truncate text-xs text-muted-foreground">{label}</p>
      </div>
    </button>
  );
}
